import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';
import { NavigationBar } from '../objects/NavigationBar';
import { transitionManager } from '../systems/TransitionManager';
import { soundManager } from '../systems/SoundManager';
import { CollectionSystem } from '../systems/CollectionSystem';

export class SettingsScene extends Phaser.Scene {
  private collectionSystem!: CollectionSystem;
  private soundEnabled = true;
  private bgmEnabled = true;
  private sfxVolume = 0.5;
  private bgmVolume = 0.25;

  constructor() {
    super({ key: 'SettingsScene' });
  }

  create(): void {
    this.collectionSystem = new CollectionSystem();
    this.soundEnabled = soundManager.isEnabled();
    this.bgmEnabled = soundManager.isBgmEnabled();
    this.sfxVolume = soundManager.getSfxVolume();
    this.bgmVolume = soundManager.getBgmVolume();

    this.addBackground();
    this.addHeader();
    this.addNavigation();
    this.addSettingsCards();
  }

  private addBackground(): void {
    const graphics = this.add.graphics();
    for (let y = 0; y < 720; y += 4) {
      const t = y / 720;
      const r = Math.floor(248 + (232 - 248) * t);
      const g = Math.floor(249 + (240 - 249) * t);
      const b = Math.floor(255 + (244 - 255) * t);
      graphics.fillStyle((r << 16) | (g << 8) | b, 1);
      graphics.fillRect(0, y, 1280, 4);
    }

    graphics.fillStyle(0xffdad8, 0.3);
    graphics.fillCircle(100, 600, 100);
    graphics.fillStyle(0x88f2e0, 0.2);
    graphics.fillCircle(1180, 120, 120);
  }

  private addHeader(): void {
    this.add.text(640, 130, 'Settings', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '48px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(640, 180, 'Customize your Toybox Arcade experience', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '18px',
      color: DESIGN.colors.onSurfaceVariant,
    }).setOrigin(0.5);
  }

  private addNavigation(): void {
    new NavigationBar(this, 0, 35, 'settings', (key) => this.handleNavigation(key));
  }

  private addSettingsCards(): void {
    const cardW = 520;
    const cardH = 80;
    const startY = 260;

    this.createToggleCard(640, startY, cardW, cardH, '🔊', 'Sound Effects', this.soundEnabled, (v) => this.toggleSound(v));
    this.createToggleCard(640, startY + 100, cardW, cardH, '🎵', 'Background Music', this.bgmEnabled, (v) => this.toggleBgm(v));
    this.createSliderCard(640, startY + 200, cardW, cardH, '🔉', 'SFX Volume', this.sfxVolume, (v) => this.setSfxVolume(v));
    this.createSliderCard(640, startY + 300, cardW, cardH, '🎚️', 'BGM Volume', this.bgmVolume, (v) => this.setBgmVolume(v));
    this.createActionCard(640, startY + 400, cardW, cardH, '🔄', 'Reset Collection', () => this.resetCollection(), 'Tap to reset');
    this.createInfoCard(640, startY + 500, cardW, cardH, 'ℹ️', 'About', 'Toybox Arcade v0.2.0');
  }

  private createToggleCard(
    x: number,
    y: number,
    w: number,
    h: number,
    icon: string,
    title: string,
    value: boolean,
    onChange: (value: boolean) => void
  ): void {
    const card = this.add.container(x, y);

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.1);
    shadow.setOrigin(0.5);
    card.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 1).setInteractive({ useHandCursor: true });
    bg.setOrigin(0.5);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    card.add(bg);

    card.add(this.add.text(-w / 2 + 50, 0, icon, { fontSize: '28px' }).setOrigin(0.5));
    card.add(this.add.text(-w / 2 + 140, 0, title, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '20px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    const toggleBg = this.add.rectangle(w / 2 - 60, 0, 56, 30, value ? parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16) : parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    toggleBg.setOrigin(0.5);
    card.add(toggleBg);

    const knob = this.add.circle(w / 2 - 60 + (value ? 12 : -12), 0, 11, 0xffffff);
    card.add(knob);

    bg.on('pointerdown', () => {
      soundManager.play('click');
      const newValue = !value;
      onChange(newValue);
      this.scene.restart();
    });
  }

  private createSliderCard(
    x: number,
    y: number,
    w: number,
    h: number,
    icon: string,
    title: string,
    value: number,
    onChange: (value: number) => void
  ): void {
    const card = this.add.container(x, y);

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.1);
    shadow.setOrigin(0.5);
    card.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 1);
    bg.setOrigin(0.5);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    card.add(bg);

    card.add(this.add.text(-w / 2 + 50, 0, icon, { fontSize: '28px' }).setOrigin(0.5));
    card.add(this.add.text(-w / 2 + 140, 0, title, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '20px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    const percent = Math.round(value * 100);
    card.add(this.add.text(w / 2 - 40, 0, `${percent}%`, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '16px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(1, 0.5));

    // Slider track
    const trackW = 160;
    const trackH = 8;
    const trackX = 80;
    const trackY = 0;
    card.add(this.add.rectangle(trackX, trackY, trackW, trackH, parseInt(DESIGN.colors.surfaceContainer.replace('#', '0x'), 16)).setOrigin(0, 0.5));
    card.add(this.add.rectangle(trackX, trackY, trackW * value, trackH, parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16)).setOrigin(0, 0.5));

    const knob = this.add.circle(trackX + trackW * value, trackY, 10, 0xffffff);
    knob.setStrokeStyle(2, parseInt(DESIGN.colors.secondary.replace('#', '0x'), 16));
    knob.setInteractive({ useHandCursor: true });
    card.add(knob);

    let dragging = false;
    knob.on('pointerdown', () => { dragging = true; });
    this.input.on('pointerup', () => { dragging = false; });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!dragging) return;
      const localX = pointer.x - x;
      const newValue = Phaser.Math.Clamp((localX - trackX) / trackW, 0, 1);
      onChange(newValue);
      this.scene.restart();
    });
    knob.on('pointerdown', () => {
      soundManager.play('click');
    });
  }

  private createActionCard(
    x: number,
    y: number,
    w: number,
    h: number,
    icon: string,
    title: string,
    onClick: () => void,
    value: string
  ): void {
    const card = this.add.container(x, y);

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.1);
    shadow.setOrigin(0.5);
    card.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 1).setInteractive({ useHandCursor: true });
    bg.setOrigin(0.5);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    card.add(bg);

    card.add(this.add.text(-w / 2 + 50, 0, icon, { fontSize: '28px' }).setOrigin(0.5));
    card.add(this.add.text(-w / 2 + 140, 0, title, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '20px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    card.add(this.add.text(w / 2 - 40, 0, value, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '16px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(1, 0.5));

    bg.on('pointerdown', () => {
      soundManager.play('click');
      onClick();
    });
  }

  private createInfoCard(
    x: number,
    y: number,
    w: number,
    h: number,
    icon: string,
    title: string,
    value: string
  ): void {
    const card = this.add.container(x, y);

    const shadow = this.add.rectangle(6, 6, w, h, 0x000000, 0.1);
    shadow.setOrigin(0.5);
    card.add(shadow);

    const bg = this.add.rectangle(0, 0, w, h, 0xffffff, 1);
    bg.setOrigin(0.5);
    bg.setStrokeStyle(1, parseInt(DESIGN.colors.outlineVariant.replace('#', '0x'), 16));
    card.add(bg);

    card.add(this.add.text(-w / 2 + 50, 0, icon, { fontSize: '28px' }).setOrigin(0.5));
    card.add(this.add.text(-w / 2 + 140, 0, title, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '20px',
      color: DESIGN.colors.onSurface,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    card.add(this.add.text(w / 2 - 40, 0, value, {
      fontFamily: DESIGN.fonts.body,
      fontSize: '16px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(1, 0.5));
  }

  private toggleSound(enabled: boolean): void {
    this.soundEnabled = enabled;
    soundManager.setEnabled(enabled);
  }

  private toggleBgm(enabled: boolean): void {
    this.bgmEnabled = enabled;
    soundManager.setBgmEnabled(enabled);
  }

  private setSfxVolume(volume: number): void {
    this.sfxVolume = volume;
    soundManager.setSfxVolume(volume);
  }

  private setBgmVolume(volume: number): void {
    this.bgmVolume = volume;
    soundManager.setBgmVolume(volume);
  }

  private resetCollection(): void {
    this.collectionSystem.reset();
    this.scene.restart();
  }

  private handleNavigation(key: string): void {
    soundManager.play('click');
    switch (key) {
      case 'play':
        transitionManager.go(this, 'GameScene');
        break;
      case 'collection':
        transitionManager.go(this, 'CollectionScene');
        break;
      case 'analytics':
        transitionManager.go(this, 'AnalyticsDashboardScene');
        break;
      case 'debug':
        transitionManager.go(this, 'DebugPanelScene');
        break;
    }
  }
}
